import Konva from "konva";
import { TextParams, BOX, SEAT } from "../Constants";
import { appendTableToList } from "../builderSetup";
import { deselectGroup, destroyTransformer, setId } from "../utils";
import { AppStage } from "@/core/AppStage";
import { mainGroups } from "@/core/MainGroups";
import { usedIds } from "@/core/UsedIds";
import { seatsGroups } from "@/core/SeatsGroup";

let builder;
let rectGroups = {};
let borderLayer = {};

const hasBoxSeatsCount = (seats) => {
  if (seats === null || seats === undefined || seats === "") return false;
  const count = parseInt(seats, 10);
  return !isNaN(count) && count > 0;
};

const getSeatsLabel = (seats) => {
  if (!hasBoxSeatsCount(seats)) return "";
  const count = parseInt(seats, 10);
  return count === 1 ? "1 seat" : `${count} seats`;
};

const getBoxDimensions = (configs) => {
  const defaultSize = BOX.defaultSize;
  const rectWidth = configs.rectW ?? configs.boxSize ?? defaultSize;
  const rectHeight = configs.rectH ?? configs.boxSize ?? defaultSize;

  return { rectWidth, rectHeight };
};

const getBoxLabels = (group) => {
  const labelsGroup = group.children.find((child) => child.attrs.type === "box_labels");
  const nameLabel = group.findOne((node) => node.attrs.type === "box_name");
  const seatsLabel = group.findOne((node) => node.attrs.type === "box_seats");

  return { labelsGroup, nameLabel, seatsLabel };
};

const positionBoxLabels = (nameLabel, seatsLabel) => {
  if (!nameLabel) return;

  const showSeats = seatsLabel && seatsLabel.visible() && seatsLabel.text();

  if (!showSeats) {
    nameLabel.setAttrs({
      x: 0,
      y: 0,
      offsetX: nameLabel.width() / 2,
      offsetY: nameLabel.height() / 2,
      rotation: 0,
    });
    return;
  }

  const gap = 8;
  const totalHeight = nameLabel.height() + gap + seatsLabel.height();

  nameLabel.setAttrs({
    x: 0,
    y: -totalHeight / 2 + nameLabel.height() / 2,
    offsetX: nameLabel.width() / 2,
    offsetY: nameLabel.height() / 2,
    rotation: 0,
  });

  seatsLabel.setAttrs({
    x: 0,
    y: -totalHeight / 2 + nameLabel.height() + gap + seatsLabel.height() / 2,
    offsetX: seatsLabel.width() / 2,
    offsetY: seatsLabel.height() / 2,
    rotation: 0,
  });
};

const syncLabelsGroup = (group, rectWidth, rectHeight, rotation, shiftX = 0, shiftY = 0) => {
  const { labelsGroup, nameLabel, seatsLabel } = getBoxLabels(group);
  const appliedRotation = rotation !== undefined ? rotation : group.rotation();

  if (labelsGroup) {
    labelsGroup.setAttrs({
      x: rectWidth / 2 + shiftX,
      y: rectHeight / 2 + shiftY,
      offsetX: 0,
      offsetY: 0,
      rotation: -appliedRotation,
    });
  }

  positionBoxLabels(nameLabel, seatsLabel);
};

const resolveBoxSeatsBind = (seatsBindInput, saved) => {
  const existing = seatsBindInput?.[0];

  if (existing?.id) {
    if (saved) {
      usedIds.add(existing.id);
    }
    return [{ id: existing.id, number: existing.number ?? 1, invalid: existing.invalid ?? false }];
  }

  const seatId = setId();
  return [{ id: seatId, number: 1, invalid: false }];
};

const createBillableSeatGroup = (rootId, seatEntry, rectWidth, rectHeight) => {
  const innerGroup = new Konva.Group();
  const seatGroup = new Konva.Group({ group: "seatGroup" });
  const seatWrapper = new Konva.Group();

  const seatRect = new Konva.Rect({
    width: rectWidth,
    height: rectHeight,
    stroke: SEAT.color,
    strokeWidth: SEAT.strokeWidth,
    x: 0,
    y: 0,
    subType: "seat",
  });

  const seatLabel = new Konva.Text({
    text: "",
    visible: false,
    width: rectWidth,
    height: rectHeight,
    subType: "seat",
  });

  seatWrapper.add(seatRect);
  seatWrapper.add(seatLabel);
  seatWrapper.setAttrs({
    id: seatEntry.id,
    groupId: rootId,
    number: seatEntry.number,
    invalid: seatEntry.invalid,
    subType: "seat",
    selected: false,
  });

  seatGroup.add(seatWrapper);
  innerGroup.add(seatGroup);

  return { innerGroup, seatGroup, seatRect };
};

const fixBox = (id, rectWidth, rectHeight, left, top, savedRotation) => {
  const currentGroup = mainGroups.get(id);
  const rectangle = rectGroups[id];
  const border = borderLayer[id];
  const transformer = currentGroup.children.find((child) => child.attrs.type === "transform");
  const rotation = savedRotation !== undefined ? savedRotation : currentGroup.rotation();
  const newX = left !== undefined ? left : currentGroup.attrs.x;
  const newY = top !== undefined ? top : currentGroup.attrs.y;

  currentGroup.setAttrs({
    width: rectWidth,
    height: rectHeight,
    offsetX: rectWidth / 2,
    offsetY: rectHeight / 2,
    x: newX,
    y: newY,
  });

  rectangle.setAttrs({
    width: rectWidth,
    height: rectHeight,
    x: 0,
    y: 0,
    offsetX: 0,
    offsetY: 0,
    scaleX: 1,
    scaleY: 1,
  });

  border.setAttrs({
    width: rectWidth + 10,
    height: rectHeight + 10,
    x: -5,
    y: -5,
    offsetX: 0,
    offsetY: 0,
  });

  if (transformer) {
    transformer.setAttrs({
      width: rectWidth,
      height: rectHeight,
      x: 0,
      y: 0,
      scaleX: 1,
      scaleY: 1,
      offsetX: 0,
      offsetY: 0,
    });
  }

  syncLabelsGroup(currentGroup, rectWidth, rectHeight, rotation);

  const updateConfigs = {
    ...currentGroup.attrs.configs,
    rectW: rectWidth,
    rectH: rectHeight,
    rotation,
  };
  currentGroup.setAttrs({ configs: updateConfigs, rotation });
  currentGroup.rotation(rotation);
};

const resizeBox = (id, transformer, start, stage) => {
  const scale = stage.scaleX();
  const currentGroup = mainGroups.get(id);
  const rotation = currentGroup.rotation();
  currentGroup.rotation(0);

  const newParams = transformer.getClientRect();
  const newWidth = (newParams.width - start[1]) / scale;
  const newHeight = (newParams.height - start[2]) / scale;
  const shiftX = (newParams.x - start[0].x) / scale;
  const shiftY = (newParams.y - start[0].y) / scale;
  const rectangle = rectGroups[id];
  const border = borderLayer[id];

  rectangle.setAttrs({
    width: newWidth,
    height: newHeight,
    offsetX: -shiftX,
    offsetY: -shiftY,
  });

  border.setAttrs({
    width: newWidth + 10,
    height: newHeight + 10,
    x: -5,
    y: -5,
    offsetX: -shiftX,
    offsetY: -shiftY,
  });

  syncLabelsGroup(currentGroup, newWidth, newHeight, rotation, shiftX, shiftY);

  const updateConfigs = {
    ...currentGroup.attrs.configs,
    rectW: newWidth,
    rectH: newHeight,
  };
  currentGroup.setAttrs({ configs: updateConfigs });

  currentGroup.rotation(rotation);
};

const recountBoxSize = (id, transformer, scale, stage) => {
  let newParams = transformer.getClientRect();
  const currentGroup = mainGroups.get(id);
  const rotation = currentGroup.rotation();
  let finalWidth = newParams.width / scale;
  let finalHeight = newParams.height / scale;
  const currentX = newParams.x / scale + finalWidth / 2 - stage.x() / scale;
  const currentY = newParams.y / scale + finalHeight / 2 - stage.y() / scale;

  if (rotation) {
    currentGroup.rotation(0);
    newParams = transformer.getClientRect();
    finalWidth = newParams.width / scale;
    finalHeight = newParams.height / scale;
  }

  fixBox(id, finalWidth, finalHeight, currentX, currentY, rotation);
};

export const generateBox = (configs, component, boxId, position, seatsBindInput) => {
  const { name, seats, rotation, hideTitle } = configs;
  const { rectWidth, rectHeight } = getBoxDimensions(configs);

  builder = component;

  const appStage = new AppStage();
  const scale = appStage.scaleX();
  const id = boxId || setId();
  const saved = Boolean(boxId);

  if (boxId) {
    usedIds.add(boxId);
  }

  const seatsBind = resolveBoxSeatsBind(seatsBindInput, saved);
  const draggable = configs.draggable !== undefined ? configs.draggable : true;
  const boxConfigs = {
    ...configs,
    rectW: rectWidth,
    rectH: rectHeight,
  };

  const mainGroup = new Konva.Group({
    draggable,
    id,
    configs: boxConfigs,
    type: "box",
    category: "seats",
    name,
    transforming: true,
    seatsBind,
  });

  const nameLabel = new Konva.Text({
    text: (name || "").toUpperCase(),
    fontSize: BOX.nameFontSize,
    fontFamily: TextParams.FONT_REGULAR,
    fill: "white",
    align: TextParams.ALIGN_CENTER,
    verticalAlign: "middle",
    type: "box_name",
    subType: "seat",
  });

  const seatsLabelText = getSeatsLabel(seats);
  const seatsLabel = new Konva.Text({
    text: seatsLabelText,
    fontSize: TextParams.SIZE_MD,
    fontFamily: TextParams.FONT_REGULAR,
    fill: "white",
    align: TextParams.ALIGN_CENTER,
    verticalAlign: "middle",
    type: "box_seats",
    subType: "seat",
    visible: Boolean(seatsLabelText),
  });

  const labelsGroup = new Konva.Group({
    type: "box_labels",
    subType: "seat",
    x: rectWidth / 2,
    y: rectHeight / 2,
  });

  labelsGroup.add(nameLabel);
  labelsGroup.add(seatsLabel);

  const { innerGroup, seatGroup, seatRect } = createBillableSeatGroup(id, seatsBind[0], rectWidth, rectHeight);

  const transformer = new Konva.Rect({
    width: rectWidth,
    height: rectHeight,
    type: "transform",
  });

  const layerBorder = new Konva.Rect({
    width: rectWidth + 10,
    height: rectHeight + 10,
    x: -5,
    y: -5,
    strokeWidth: 1,
    dash: [5, 5],
    type: "border",
  });

  mainGroup.add(innerGroup);
  mainGroup.add(labelsGroup);
  mainGroup.add(transformer);
  mainGroup.add(layerBorder);
  mainGroups.add(mainGroup, id);
  mainGroup.seatsBind = seatsBind;
  seatsGroups.add(seatGroup, id);

  rectGroups[id] = seatRect;
  borderLayer[id] = layerBorder;

  positionBoxLabels(nameLabel, seatsLabel);

  if (hideTitle === true) {
    nameLabel.visible(false);
    seatsLabel.visible(false);
  }

  layerBorder.moveToTop();

  mainGroup.setAttrs({
    width: rectWidth,
    height: rectHeight,
    offsetX: rectWidth / 2,
    offsetY: rectHeight / 2,
  });

  let startSize = 0;
  let startXDefence = 0;
  let startYDefence = 0;

  transformer.on("transformstart", () => {
    const currentGroup = mainGroups.get(id);
    const currentConfigs = currentGroup.attrs.configs;
    const currentRotation = currentGroup.rotation();
    currentGroup.rotation(0);
    startSize = transformer.getClientRect();
    currentGroup.rotation(currentRotation);
    startXDefence = startSize.width - currentConfigs.rectW * appStage.scaleX();
    startYDefence = startSize.height - currentConfigs.rectH * appStage.scaleX();
  });

  transformer.on("transform", () => {
    resizeBox(id, transformer, [startSize, startXDefence, startYDefence], appStage);
  });

  transformer.on("transformend", () => {
    recountBoxSize(id, transformer, appStage.scaleX(), appStage);
  });

  if (position) {
    mainGroup.position(position);
  } else {
    const mainGroupPos = mainGroup.getClientRect();

    mainGroup.position({
      x: (-appStage.x() + appStage.width() / 2) / scale,
      y: (-appStage.y() + 30) / scale + (mainGroupPos.height / 2) * scale,
    });
  }

  if ("rotation" in configs && rotation !== null) {
    rotateBox(rotation, id, boxConfigs);
  }

  if (appStage.editing && mainGroup.attrs.draggable) {
    mainGroup.setAttrs({
      draggable: false,
      editDrag: true,
    });
  }

  component.countTickets(id, 1, "boxes");

  return { mainGroup };
};

export const rotateBox = (rotation, id, configs) => {
  const currentGroup = mainGroups.get(id);
  const { rectWidth, rectHeight } = getBoxDimensions(currentGroup.attrs.configs);

  currentGroup.rotation(rotation);
  syncLabelsGroup(currentGroup, rectWidth, rectHeight, rotation);

  currentGroup.setAttrs({ configs: { ...configs, rotation } });
};

export const updateBoxStage = (id, payload) => {
  const { name, seats } = payload.newData;
  const currentGroup = mainGroups.get(id);
  const { nameLabel, seatsLabel } = getBoxLabels(currentGroup);
  const { rectWidth, rectHeight } = getBoxDimensions(currentGroup.attrs.configs);

  if (nameLabel) {
    nameLabel.text((name || "").toUpperCase());
  }

  if (seatsLabel) {
    const seatsLabelText = getSeatsLabel(seats);
    seatsLabel.text(seatsLabelText);
    seatsLabel.visible(Boolean(seatsLabelText));
  }

  syncLabelsGroup(currentGroup, rectWidth, rectHeight);

  const updateConfigs = { ...currentGroup.attrs.configs, name, seats };
  currentGroup.setAttrs({ configs: updateConfigs, name });
};

export const cancelBoxChanges = (configs) => {
  const appStage = new AppStage();
  const mainLayer = appStage.children[0];
  const layerId = configs?.layerId;
  const currentGroup = mainGroups.get(layerId);
  const position = currentGroup.position();
  const seatsBind = currentGroup.attrs.seatsBind || currentGroup.seatsBind;

  currentGroup.destroy();

  const { mainGroup } = generateBox(configs, builder, layerId, position, seatsBind);
  mainLayer.add(mainGroup);
  deselectGroup();
};

const copyBox = (id, position, { deselect = false } = {}) => {
  const currentGroup = mainGroups.get(id);
  const appStage = new AppStage();
  const mainLayer = appStage.children[0];
  const rotation = currentGroup.rotation();
  currentGroup.rotation(0);

  const conf = { ...currentGroup.attrs.configs, draggable: true };
  const { mainGroup } = generateBox(conf, builder, null, position);

  if (rotation) {
    currentGroup.rotation(rotation);
  }

  mainLayer.add(mainGroup);
  if (deselect) {
    deselectGroup();
  }
  appendTableToList(mainGroups);

  return mainGroup;
};

export const copyBoxElementAtPosition = (id, component, position) => {
  builder = component;
  const mainGroup = copyBox(id, position);
  return mainGroup.attrs.id;
};

export const boxActions = {
  editAction: (id, component) => {
    const currentGroup = mainGroups.get(id);
    const confs = currentGroup.attrs.configs;

    component.editBox(id, confs, currentGroup);
    destroyTransformer();
  },
  copyAction: (id) => {
    copyBox(id, null, { deselect: true });
  },
};
